import {
    BoxProps,
    Tab,
    TabList,
    TabListProps,
    TabPanel,
    TabPanels,
    Tabs,
    TabsProps,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobileOrTablet } from '../../hooks/UseResponsive/UseResponsive';
import CanopyTooltip from '../CanopyTooltip/CanopyTooltip';
import SpinnerOverlay from '../SpinnerOverlay/SpinnerOverlay';

export type TabType = {
    title: string;
    path: string;
    key: string;
};

export interface CanopyTabsProps {
    tabList: TabType[];
    onTabSelect?: (path: string) => void;
    tabsProps?: TabsProps;
    tabListProps?: TabListProps;
    tabProps?: BoxProps & React.ButtonHTMLAttributes<any>;
    activeTabIndex: number;
    globalProps?: any;
    isNavBarExpanded: boolean;
    isBannerShown: boolean;
    children?: React.ReactNode;
}

const CanopyTabs: React.FC<CanopyTabsProps> = ({
    tabList,
    onTabSelect,
    tabsProps,
    tabListProps,
    tabProps,
    activeTabIndex,
    children,
    globalProps = {},
    isNavBarExpanded,
    isBannerShown,
}) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const currentColor = useCurrentColors();
    const { t, qp } = globalProps;
    const isMobile = useCheckForMobileOrTablet();
    const [localTabList, setLocalTablist] = useState<TabType[]>(tabList);
    useEffect(() => {
        if (activeTabIndex >= 0) {
            setLocalTablist(tabList);
            setSelectedTab(activeTabIndex);
        }
    }, [activeTabIndex, tabList]);

    const getTabTranslatedLabel = (tab: any): string =>
        tab.path.startsWith('/tableau-analytics/')
            ? tab.title
            : t(`ui.mm.sub-menu-${tab.key}`);

    const topPosition = isNavBarExpanded ? 130 : 70;
    return (
        <Tabs
            position="relative"
            mt="45px"
            isFitted
            width="100%"
            index={selectedTab}
            {...tabsProps}>
            <TabList
                position="fixed"
                top={`${isBannerShown ? topPosition + 75 : topPosition}px`}
                left={isMobile ? '0' : '80px'}
                width={isMobile ? '100%' : 'calc(100% - 80px)'}
                overflow="auto"
                right="0px"
                zIndex={100}
                background={currentColor.pageBg}
                borderBottom={`1px solid ${currentColor.brand['600']}`}
                overflowY="hidden"
                {...tabListProps}>
                {localTabList?.map((tab) => {
                    return (
                        <Tab
                            {...tabProps}
                            key={tab.path}
                            pt="12px"
                            pb="12px"
                            backgroundColor="transparent"
                            textTransform="uppercase"
                            textAlign="center"
                            fontSize="12px"
                            fontWeight="900"
                            color={currentColor.whiteAlpha['400']} // same in light and dark mode
                            letterSpacing="0.3px"
                            outline="none"
                            boxShadow="none !important" // Remove outline
                            whiteSpace="nowrap"
                            flexGrow={0}
                            _focus={{ outline: 'none' }}
                            borderBottom="4px solid transparent"
                            _selected={{
                                color: currentColor.brand['10'],
                                borderColor: currentColor.primaryColor,
                                borderBottomWidth: '4px',
                            }}
                            _first={isMobile ? {} : { marginLeft: '20px' }}>
                            <CanopyTooltip
                                label={
                                    tab.key === 'New Tab'
                                        ? t('new-tab')
                                        : getTabTranslatedLabel(tab)
                                }
                                fontSize="12px"
                                color={currentColor.brand['10']}
                                width="100%"
                                minWidth="100%"
                                maxWidth="150px"
                                className="two-line-ellipsis"
                                cursor={'pointer'}>
                                <Link to={`${tab.path}`}>
                                    {tab.key === 'New Tab'
                                        ? t('new-tab')
                                        : getTabTranslatedLabel(tab)}
                                </Link>
                            </CanopyTooltip>
                        </Tab>
                    );
                })}
            </TabList>
            <TabPanels>
                {localTabList?.map((tab, index) => {
                    return (
                        <TabPanel
                            p={0}
                            key={tab.path}>
                            {selectedTab === index ? (
                                children
                            ) : (
                                <SpinnerOverlay
                                    zIndex="inherit"
                                    position="absolute"
                                    text={t('loading')}></SpinnerOverlay>
                            )}
                        </TabPanel>
                    );
                })}
            </TabPanels>
        </Tabs>
    );
};

export default CanopyTabs;
